import { serialize } from 'cookie';

import { EMAIL, SMS } from '@/constants/subscriptions';
import clearTimeFromDate from '@/utils/helpers/clear-time-from-date';
import { numToFixed } from '@/utils/helpers/number-utils';
import { calcDistanceByLocations } from '@/utils/location/distance-utils';
import { getGeoCoords } from '@/utils/location/geo-utils';

const clearTime = (promo) => promo
  .map((promoItem) => ({
    ...promoItem,
    dateBegin: clearTimeFromDate(new Date(promoItem.dateBegin)).toISOString(),
    dateEnd: clearTimeFromDate(new Date(promoItem.dateEnd)).toISOString(),
  }));

const sortByBegin = (promo) => promo
  .sort((a, b) => new Date(b.dateBegin) - new Date(a.dateBegin));

export default {
  namespaced: true,

  state: () => ({
    city: {},
    defaultCityId: '1720920299',
    cityConfirmed: false,
    account: {},
    addresses: [],
    promos: [],
    coupons: [],
    geoCoords: null,
    needMergeCarts: false,
  }),

  getters: {
    city: (state) => state.city,
    cityId: (state) => state.city.id,
    cityName: (state) => state.city.name,
    cityConfirmed: (state) => state.cityConfirmed,
    account: (state) => state.account,
    accountCity: (state) => state.account.city || {},
    addresses: (state) => state.addresses,
    subscriptions: (state) => state.account.subscriptions || [],
    isSubscriptionsNotActive: (state) => state.account.subscriptions?.every((subscription) => !subscription.active),
    loggedIn: (state) => !!state.account.id,
    calcDistanceToUser: (state) => (coords) => (state.geoCoords && coords
      ? numToFixed(calcDistanceByLocations(state.geoCoords, coords), 1)
      : ''),
    // Email
    confirmedEmail: (state) => state.account.confirmedEmail,
    isEmailConfirmed: (state) => state.account.emailConfirmed,
    unconfirmedEmail: (state) => state.account.unconfirmedEmail,
    needToConfirmNew: (state, getters) => !getters.confirmedEmail
      && (getters.account.email !== getters.unconfirmedEmail),
    // Акции и промокоды
    promos: (state) => state.promos,
    promosCount: (state) => state.promos.length,
    coupons: (state) => state.coupons,
    couponsCount: (state) => state.coupons.length,
    cashbackBonus: (state) => state.account.bonuses?.cashback || {},
    actionsBonus: (state) => state.account.bonuses?.actions || {},
    userBonusCardNumber: (state) => state.account.bonus?.cardNumber || '',
    promoBonusesAtDate: (state) => (date) => {
      const argDate = clearTimeFromDate(date);
      return state.promos
        .filter((promo) => {
          const promoStart = clearTimeFromDate(new Date(promo.dateBegin));
          const promoEnd = clearTimeFromDate(new Date(promo.dateEnd));

          return argDate >= promoStart && promoEnd >= argDate
            && promo.bonusType === 'action';
        })
        .reduce((acc, { bonuses }) => acc + parseInt(bonuses), 0);
    },
    bonuses: (state) => Math.floor(state.account?.bonuses?.general?.amount || 0),
    // sort мутирует и вызывает бесконечный цикл рендера по этому создаем новый массив
    promosSortByEnd: (state, getters) => [...getters.promos].sort((a, b) => new Date(a.dateEnd) - new Date(b.dateEnd)),
    // Корзина
    cartIds: (state) => state.account?.cartIds ?? [],
    anonymousCart: (_, getters) => getters.cartIds?.filter(({ isAnonymous }) => isAnonymous).at(0) ?? {},
    authCart: (_, getters) => getters.cartIds?.filter(({ isAnonymous }) => !isAnonymous).at(0) ?? {},
    isNeedMergeCarts: (state) => state.needMergeCarts,
  },

  mutations: {
    setCity: (state, city = {}) => {
      state.city = city;
    },
    setCityId: (state, id) => {
      state.city = {
        ...state.city,
        id,
      };
    },
    cityConfirmed: (state, confirmed = true) => {
      state.cityConfirmed = confirmed;
    },
    setAccount: (state, account) => {
      const phone = account.phone || '';

      state.account = { ...account, phone: phone.replace(/\D/g, '') };
      state.account.subscriptions = state.account.subscriptions
        ?.filter(({ code }) => [SMS, EMAIL].includes(code)) || [];
    },
    setAddresses: (state, addresses) => {
      state.addresses = addresses;
    },
    setSubscriptions: (state, subs) => {
      state.account.subscriptions = subs;
    },
    setPromos(state, promos) {
      state.promos = promos;
    },
    setCoupons(state, coupons) {
      state.coupons = coupons;
    },
    setEmailData(state, {
      unconfirmedEmail, confirmedEmail, emailConfirmed, email,
    }) {
      state.account = {
        ...state.account, unconfirmedEmail, confirmedEmail, emailConfirmed, email,
      };
    },
    setGeoCoords: (state, coords) => {
      state.geoCoords = coords;
    },
    setNeedMergeCarts: (state, value = false) => {
      state.needMergeCarts = value;
    },
  },

  actions: {
    updateCity({ commit, dispatch }, city = {}) {
      commit('setCity', city);
      dispatch('setClientCookie');
    },

    detectCity({ dispatch }) {
      // вызываем только на клиенте, тк нужен ip
      return this.$api.detectCity()
        .then((city) => {
          dispatch('updateCity', city);
        })
        .catch(() => {});
    },

    getCity({ commit, dispatch }) {
      return this.$api.getCities()
        .then(([city]) => {
          if (city && city.id) {
            commit('setCity', city);
          } else {
            dispatch('getDefaultCity');
          }
        })
        .catch(() => {
          dispatch('getDefaultCity');
        });
    },

    getDefaultCity({ commit, state }) {
      return this.$api.getCities([state.defaultCityId])
        .then(([city]) => {
          if (city && city.id) {
            commit('setCity', city);
            commit('cityConfirmed', false);
          }
        });
    },

    loadAddresses({ commit }) {
      return this.$api.getAddresses()
        .then((addresses) => {
          commit('setAddresses', addresses);
        });
    },

    setClientCookie({ state }) {
      const cookieOptions = {
        maxAge: 31556952000, // 1Y
        path: '/',
        sameSite: 'lax',
      };
      document.cookie = serialize('CITY_ID', state.city.id, cookieOptions);
    },

    setSubscriptions({ commit }, subs = []) {
      commit('setSubscriptions', subs);
    },

    onLogin({ dispatch, commit, getters }, { account }) {
      commit('setAccount', mapAccount(account));

      commit('setNeedMergeCarts', getters.cartIds.length > 1);

      return Promise.allSettled([
        dispatch('cart/loadCartAfterLogin', { cartIds: getters.cartIds, territoryId: getters.cityId }, { root: true }),
        dispatch('favorites/loadFavoritesShort', null, { root: true }),
        dispatch('cart/trackCartStats', null, { root: true }),
      ]);
    },

    getAccount({ commit }) {
      // Первый запрос с бека, отрабатывает для перевыпуска токена!
      return this.$apiBFF.getAccount().then((account) => {
        commit('setAccount', account);
      }).catch((e) => {
        /* протухший токен */
        if (e.response?.status === 400) throw e;
      });
    },

    updateProfile({ commit }, form) {
      return this.$api.updateAccount({ form }).then((account) => {
        commit('setAccount', account);
      });
    },

    loadCoupons({ commit }) {
      return this.$api.getCoupons().then((coupons) => {
        commit('setCoupons', sortByBegin(clearTime(coupons)));
      })
        .catch(() => {
          this.$logger.warn('Не удалось загрузить купоны');
        });
    },

    loadPromos({ commit }) {
      return this.$api.getPromotions().then((promotions) => {
        commit('setPromos', sortByBegin(clearTime(promotions)));
      })
        .catch(() => {
          this.$logger.warn('Не удалось загрузить акции');
        });
    },

    async sendConfirmEmail({ dispatch }, { email }) {
      try {
        await this.$api.sendConfirmEmail({ email });

        dispatch('notifications/create', {
          type: 'success',
          title: 'Успешно!',
          message: 'Письмо для подтверждения Email успешно отправлено',
        }, { root: true });
      } catch (e) {
        dispatch('notifications/create', {
          type: 'error',
          title: 'Ошибка',
          message: 'При выполнении запроса произошла ошибка',
        }, { root: true });

        return Promise.reject(e);
      }
    },

    async changeEmail({ commit }, newEmail) {
      try {
        const {
          status, unconfirmedEmail, confirmedEmail, emailConfirmed, email,
        } = await this.$api.changeEmail(newEmail);

        if (status === 400) {
          return Promise.reject('Введённый Email уже существует в системе');
        }

        commit('setEmailData', {
          unconfirmedEmail, confirmedEmail, emailConfirmed, email,
        });
      } catch (e) {
        return Promise.reject(e?.errors?.[0]?.title || '');
      }
    },

    async getGeoCoords({ state, commit }) {
      try {
        const coords = await getGeoCoords();
        commit(
          'setGeoCoords',
          coords ? { latitude: coords.latitude, longitude: coords.longitude } : null,
        );
      } catch (e) {
        commit('setGeoCoords', null);
      }

      return state.geoCoords;
    },
  },
};

/**
 * Функция маппинга аккаунта при логине/регистрации из BFF в формат Остин
 * Удалить при переезде аккаунта на BFF
 */
function mapAccount(account) {
  return {
    ...account,
    confirmedEmail: account.emailConfirmed ? account.email : undefined,
    unconfirmedEmail: !account.emailConfirmed ? account.unconfirmedEmail : undefined,
    bonuses: {
      general: {
        amount: account.bonuses,
      },
    },
  };
}

